import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Download,
  FileText,
  FileJson,
  FileSpreadsheet,
  Eye,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Info,
} from "lucide-react";
import { toast } from "sonner";

export default function TaxExport() {
  return (
    <DashboardLayout>
      <TaxExportContent />
    </DashboardLayout>
  );
}

function TaxExportContent() {
  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear() - 1
  );
  const [selectedFormat, setSelectedFormat] = useState<
    | "xml"
    | "json"
    | "transaction_csv"
    | "deduction_csv"
    | "deduction_pdf"
    | "tax_report_pdf"
  >("xml");
  const [showPreview, setShowPreview] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  // Get current company (assuming first company for now)
  const { data: companies } = trpc.company.list.useQuery();
  const companyId = companies?.[0]?.id || 1;

  // Get export stats
  const { data: stats, isLoading: statsLoading } =
    trpc.taxExport.getExportStats.useQuery({
      companyId,
      taxYear: selectedYear,
    });

  // Get preview
  const {
    data: preview,
    isLoading: previewLoading,
    refetch: refetchPreview,
  } = trpc.taxExport.previewExport.useQuery(
    {
      companyId,
      taxYear: selectedYear,
      format: selectedFormat,
    },
    {
      enabled: showPreview,
    }
  );

  // Validation query
  const {
    data: validationReport,
    isLoading: validationLoading,
    refetch: refetchValidation,
  } = trpc.taxExport.validateExport.useQuery(
    {
      companyId,
      taxYear: selectedYear,
      format: selectedFormat,
    },
    {
      enabled: showValidation,
    }
  );

  // Export mutation
  const exportMutation = trpc.taxExport.exportTaxData.useMutation({
    onSuccess: data => {
      // Decode base64 and trigger download
      const binary = atob(data.content);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: data.mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(`Export erfolgreich! / Export successful!`, {
        description: `${data.filename} (${(data.size / 1024).toFixed(2)} KB)`,
      });
    },
    onError: error => {
      toast.error("Export fehlgeschlagen / Export failed", {
        description: error.message,
      });
    },
  });

  const handleExport = () => {
    exportMutation.mutate({
      companyId,
      taxYear: selectedYear,
      format: selectedFormat,
    });
  };

  const handlePreview = () => {
    setShowPreview(true);
    refetchPreview();
  };

  const handleValidate = () => {
    setShowValidation(true);
    refetchValidation();
  };

  const formatOptions = [
    {
      value: "xml",
      label: "Swiss Tax XML (eCH-0217)",
      icon: FileText,
      description: "Electronic submission to tax authority",
    },
    {
      value: "json",
      label: "JSON Format",
      icon: FileJson,
      description: "Modern tax software compatible",
    },
    {
      value: "transaction_csv",
      label: "Transaction Journal CSV",
      icon: FileSpreadsheet,
      description: "Detailed transaction listing",
    },
    {
      value: "deduction_csv",
      label: "Deduction Summary CSV",
      icon: FileSpreadsheet,
      description: "Applied deductions list",
    },
    {
      value: "deduction_pdf",
      label: "Deduction Summary PDF",
      icon: FileText,
      description: "Professional deduction report",
    },
    {
      value: "tax_report_pdf",
      label: "Tax Declaration PDF",
      icon: FileText,
      description: "Comprehensive tax report",
    },
  ];

  const currentFormat = formatOptions.find(f => f.value === selectedFormat);

  // Generate year options (current year - 5 to current year)
  const yearOptions = Array.from(
    { length: 6 },
    (_, i) => new Date().getFullYear() - i
  );

  return (
    <div className="container py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Steuerexport / Tax Export</h1>
        <p className="text-muted-foreground">
          Export tax data in formats compatible with Swiss tax filing software
        </p>
      </div>

      {/* Export Configuration */}
      <div className="grid gap-6 md:grid-cols-2 mb-6">
        <Card>
          <CardHeader>
            <CardTitle>Export Configuration</CardTitle>
            <CardDescription>Select tax year and export format</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">
                Steuerjahr / Tax Year
              </label>
              <Select
                value={selectedYear.toString()}
                onValueChange={value => {
                  setSelectedYear(parseInt(value));
                  setShowPreview(false);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map(year => (
                    <SelectItem key={year} value={year.toString()}>
                      {year === new Date().getFullYear()
                        ? `${year} (YTD)`
                        : year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">
                Exportformat / Export Format
              </label>
              <Select
                value={selectedFormat}
                onValueChange={(value: any) => {
                  setSelectedFormat(value);
                  setShowPreview(false);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {formatOptions.map(format => (
                    <SelectItem key={format.value} value={format.value}>
                      <div className="flex items-center gap-2">
                        <format.icon className="h-4 w-4" />
                        {format.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {currentFormat && (
                <p className="text-sm text-muted-foreground mt-2">
                  {currentFormat.description}
                </p>
              )}
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                onClick={handleExport}
                disabled={exportMutation.isPending}
                className="flex-1"
              >
                <Download className="h-4 w-4 mr-2" />
                {exportMutation.isPending ? "Exporting..." : "Export"}
              </Button>
              <Button
                onClick={handlePreview}
                variant="outline"
                disabled={previewLoading}
              >
                <Eye className="h-4 w-4 mr-2" />
                Preview
              </Button>
              <Button
                onClick={handleValidate}
                variant="outline"
                disabled={validationLoading}
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Validate
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Export Statistics */}
        <Card>
          <CardHeader>
            <CardTitle>Export Statistics</CardTitle>
            <CardDescription>Data included in this export</CardDescription>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="text-sm text-muted-foreground">Loading...</div>
            ) : stats ? (
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">
                    Company:
                  </span>
                  <span className="text-sm font-medium">{stats.company}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">
                    Tax Year:
                  </span>
                  <span className="text-sm font-medium">{stats.taxYear}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">
                    Transactions:
                  </span>
                  <span className="text-sm font-medium">
                    {stats.transactionCount}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">
                    Deductions:
                  </span>
                  <span className="text-sm font-medium">
                    {stats.deductionCount}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">
                    Total Deductions:
                  </span>
                  <span className="text-sm font-medium">
                    {new Intl.NumberFormat("de-CH", {
                      style: "currency",
                      currency: "CHF",
                    }).format(stats.totalDeductionAmount / 100)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">
                    Net Profit:
                  </span>
                  <span className="text-sm font-medium">
                    {new Intl.NumberFormat("de-CH", {
                      style: "currency",
                      currency: "CHF",
                    }).format(stats.netProfit / 100)}
                  </span>
                </div>
                {stats.vatPayable !== 0 && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">
                      VAT Payable:
                    </span>
                    <span className="text-sm font-medium">
                      {new Intl.NumberFormat("de-CH", {
                        style: "currency",
                        currency: "CHF",
                      }).format(stats.vatPayable / 100)}
                    </span>
                  </div>
                )}
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      {/* Validation Report Section */}
      {showValidation && validationReport && (
        <Card
          className={
            validationReport.valid
              ? "border-[var(--success)]"
              : "border-destructive"
          }
        >
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  {validationReport.valid ? (
                    <CheckCircle2 className="h-5 w-5 text-[var(--success)]" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-destructive" />
                  )}
                  Validation Report
                </CardTitle>
                <CardDescription>
                  {validationReport.valid
                    ? "Export data is valid and ready for submission"
                    : "Issues found - please review before exporting"}
                </CardDescription>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold">
                  {validationReport.summary.errorCount === 0 ? (
                    <span className="text-[var(--success)]">✓</span>
                  ) : (
                    <span className="text-destructive">
                      {validationReport.summary.errorCount}
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">Errors</div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4 p-4 bg-muted rounded-lg">
              <div className="text-center">
                <div className="text-2xl font-bold text-destructive">
                  {validationReport.summary.errorCount}
                </div>
                <div className="text-xs text-muted-foreground">Errors</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-[var(--warning)]">
                  {validationReport.summary.warningCount}
                </div>
                <div className="text-xs text-muted-foreground">Warnings</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-[var(--info)]">
                  {validationReport.summary.infoCount}
                </div>
                <div className="text-xs text-muted-foreground">Info</div>
              </div>
            </div>

            {/* Errors */}
            {validationReport.errors.length > 0 && (
              <div>
                <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-destructive" />
                  Errors ({validationReport.errors.length})
                </h4>
                <div className="space-y-2">
                  {validationReport.errors.map((issue, index) => (
                    <div
                      key={index}
                      className="p-3 border border-destructive/20 bg-destructive/10 rounded-lg"
                    >
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-destructive mt-0.5" />
                        <div className="flex-1">
                          <div className="font-medium text-sm">
                            {issue.message}
                          </div>
                          {issue.field && (
                            <div className="text-xs text-muted-foreground mt-1">
                              Field: {issue.field}
                            </div>
                          )}
                          {issue.suggestion && (
                            <div className="text-xs text-[var(--info)] mt-1">
                              💡 {issue.suggestion}
                            </div>
                          )}
                        </div>
                        <span className="text-xs font-mono bg-destructive/20 px-2 py-1 rounded">
                          {issue.code}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Warnings */}
            {validationReport.warnings.length > 0 && (
              <div>
                <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-[var(--warning)]" />
                  Warnings ({validationReport.warnings.length})
                </h4>
                <div className="space-y-2">
                  {validationReport.warnings.map((issue, index) => (
                    <div
                      key={index}
                      className="p-3 border border-[var(--warning)]/20 bg-[var(--warning)]/10 rounded-lg"
                    >
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 text-[var(--warning)] mt-0.5" />
                        <div className="flex-1">
                          <div className="font-medium text-sm">
                            {issue.message}
                          </div>
                          {issue.field && (
                            <div className="text-xs text-muted-foreground mt-1">
                              Field: {issue.field}
                            </div>
                          )}
                          {issue.suggestion && (
                            <div className="text-xs text-[var(--info)] mt-1">
                              💡 {issue.suggestion}
                            </div>
                          )}
                        </div>
                        <span className="text-xs font-mono bg-[var(--warning)]/20 px-2 py-1 rounded">
                          {issue.code}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Info */}
            {validationReport.info.length > 0 && (
              <div>
                <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                  <Info className="h-4 w-4 text-[var(--info)]" />
                  Information ({validationReport.info.length})
                </h4>
                <div className="space-y-2">
                  {validationReport.info.map((issue, index) => (
                    <div
                      key={index}
                      className="p-3 border border-[var(--info)]/20 bg-[var(--info)]/10 rounded-lg"
                    >
                      <div className="flex items-start gap-2">
                        <Info className="h-4 w-4 text-[var(--info)] mt-0.5" />
                        <div className="flex-1">
                          <div className="font-medium text-sm">
                            {issue.message}
                          </div>
                          {issue.field && (
                            <div className="text-xs text-muted-foreground mt-1">
                              Field: {issue.field}
                            </div>
                          )}
                        </div>
                        <span className="text-xs font-mono bg-[var(--info)]/20 px-2 py-1 rounded">
                          {issue.code}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Preview Section */}
      {showPreview && preview && (
        <Card>
          <CardHeader>
            <CardTitle>Export Preview</CardTitle>
            <CardDescription>
              {preview.truncated
                ? `Showing first 50 lines of ${preview.totalLines} total lines`
                : `All ${preview.totalLines} lines`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-xs font-mono max-h-96 overflow-y-auto">
              {preview.preview}
            </pre>
            {preview.truncated && (
              <p className="text-sm text-muted-foreground mt-2">
                Full content will be included in the downloaded file
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Format Information */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Export Formats</CardTitle>
          <CardDescription>
            Information about available export formats
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            {formatOptions.map(format => (
              <div
                key={format.value}
                className="flex gap-3 p-3 border rounded-lg"
              >
                <format.icon className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <h4 className="font-medium text-sm">{format.label}</h4>
                  <p className="text-xs text-muted-foreground">
                    {format.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
